import axios, { AxiosInstance } from 'axios';
import axiosRetry from 'axios-retry';
import Sentry from 'src/services/sentry';

import axiosInstance, {
  CAMERA_SIMULATOR_IP,
  CAMERA_STATIC_IP,
  CameraApiEnum,
} from './AxiosInstance';
import { GetSettingsResponse } from './Settings/GetSettings.model';
import { GetLanguagesResponse } from './Settings/Language/GetLanguages.model';
import { SetLanguageParams } from './Settings/Language/SetLanguage.model';
import {
  AutoStateResponse,
  AutoTakePhotoRequest,
  GetDeviceSNResponse,
  ShootingResponse,
} from './Shooting/Shooting.model';
import {
  GainResponse,
  GetBatteryInfoResponse,
  GetStorageResponse,
  GetVersionInfoResponse,
  GetVolumeResponse as VolumeResponse,
} from './System/System.model';

class CameraConnection {
  static #instance: CameraConnection | null = null;
  #cameraIp: string;
  #baseUrl: string;
  #axiosInstance: AxiosInstance = axiosInstance;

  constructor(cameraIp: string) {
    this.#cameraIp = cameraIp;
    this.#baseUrl = `http://${cameraIp}:8901`;
    this.#axiosInstance.defaults.baseURL = this.#baseUrl;
  }

  #log(message: string, attributes?: Record<string, unknown>) {
    Sentry.logger.info(message, {
      ...attributes,
      cameraIp: this.#cameraIp,
    });
  }

  public static getInstance(
    cameraIp?: string,
    cameraIpMissingMsg?: string,
  ): CameraConnection {
    if (CameraConnection.#instance) {
      if (cameraIp && cameraIp !== CameraConnection.#instance.#cameraIp) {
        CameraConnection.#instance.setCameraIp(cameraIp);
      }
      return CameraConnection.#instance;
    }

    if (!cameraIp) {
      throw new Error(cameraIpMissingMsg);
    }

    CameraConnection.#instance = new CameraConnection(cameraIp);

    CameraConnection.#instance.#log(
      `Camera instance has been created at ${cameraIp}`,
    );

    return CameraConnection.#instance;
  }

  public getCameraIp(): string {
    return this.#cameraIp;
  }

  public setCameraIp(cameraIp: string): void {
    this.#cameraIp = cameraIp;
    this.#axiosInstance.defaults.baseURL = `http://${cameraIp}:8901`;
    this.#log(`Camera IP has been changed to ${cameraIp}`);
  }

  public static getCameraIpList = async (
    localIP: string[] | string,
    signal: AbortSignal,
  ) => {
    console.log('localIP:', localIP);

    Sentry.logger.info(`localIP: ${JSON.stringify(localIP)}`);

    const generateIpRange = (ip: string): string[] =>
      Array.from(
        { length: 254 },
        (_, i) =>
          `${ip.slice(0, Math.max(0, ip.lastIndexOf('.') + 1))}${i + 1}`,
      );

    const ipList: string[] = Array.isArray(localIP)
      ? localIP.flatMap((ip) => generateIpRange(ip))
      : generateIpRange(localIP);

    Sentry.logger.info(`IPListBlock: ${JSON.stringify(ipList)}`);
    console.log('IPList:', ipList);

    for (const ip of [CAMERA_SIMULATOR_IP, CAMERA_STATIC_IP]) {
      if (!ipList.includes(ip)) {
        ipList.unshift(ip);
      }
    }

    const client = axios.create();

    axiosRetry(client, {
      retries: 3,
      shouldResetTimeout: true,
      retryCondition: () => true,
      retryDelay: () => 1000,
    });

    const promiseList = ipList.map((ip) =>
      client.get<GetVersionInfoResponse>(
        `http://${ip}:8901${CameraApiEnum.GET_VERSIONS}`,
        {
          timeout: 10_000,
          signal,
        },
      ),
    );

    const results = await Promise.allSettled(promiseList);

    const cameraIpList = results.reduce((pre, curItem, curIndex) => {
      if (curItem.status === 'fulfilled') {
        pre.push(ipList[curIndex]);
      }
      return pre;
    }, [] as string[]);

    Sentry.logger.info(
      `Camera IP list has been fetched: ${JSON.stringify(cameraIpList)}`,
    );

    return cameraIpList;
  };

  public setVolume = async (volume: number) => {
    const response = await this.#axiosInstance.get<VolumeResponse>(
      CameraApiEnum.SET_VOLUME,
      {
        params: {
          Num: volume,
        },
      },
    );

    this.#log(
      `Camera volume has been set to ${volume}: ${JSON.stringify(response)}`,
    );

    return response;
  };

  public getLanguages = async () => {
    const response = await this.#axiosInstance.get<GetLanguagesResponse>(
      CameraApiEnum.GET_ALL_LANGUAGES,
    );

    this.#log(
      `Camera languages have been fetched: ${JSON.stringify(response)}`,
    );

    return response;
  };

  public setLanguage = async (params: SetLanguageParams) => {
    const response = await this.#axiosInstance.get<GetLanguagesResponse>(
      CameraApiEnum.SET_LANGUAGE,
      {
        params,
      },
    );

    this.#log(`Camera language has been set: ${JSON.stringify(response)}`);

    return response;
  };

  public autoTakeImage = async (params?: AutoTakePhotoRequest) => {
    const response = await this.#axiosInstance.get<
      AutoTakePhotoRequest,
      ShootingResponse
    >(CameraApiEnum.AUTO_TAKE_PHOTO, { params });

    this.#log(
      `Camera auto take image has been called: ${JSON.stringify(response)}`,
    );
    return response;
  };

  public existCaptureProcess = async () => {
    const response = await this.#axiosInstance.get<ShootingResponse>(
      CameraApiEnum.EXIST_CAPTURE_PROCESS,
    );

    this.#log(
      `Camera exist capture process has been called: ${JSON.stringify(
        response,
      )}`,
    );

    return response;
  };

  public continueCaptureProcess = async () => {
    const response = await this.#axiosInstance.get<ShootingResponse>(
      CameraApiEnum.CONTINUE_CAPTURE_PROCESS,
    );

    this.#log(
      `Camera continue capture process has been called: ${JSON.stringify(
        response,
      )}`,
    );

    return response;
  };

  public getCurrentCaptureProcess = async () => {
    const response = await this.#axiosInstance.get<AutoStateResponse>(
      CameraApiEnum.GET_CURRENT_CAPTURE_PROCESS,
    );

    this.#log(
      `Camera current capture process has been loaded: ${JSON.stringify(
        response,
      )}`,
    );

    return response;
  };

  public skipCurrentEye = async () => {
    this.#log('Camera skip current eye has been called');
    return await this.#axiosInstance.get<ShootingResponse>(
      CameraApiEnum.SKIP_CURRENT_EYE,
      {
        params: {
          ignore: false,
        },
      },
    );
  };

  public getVolume = async () => {
    return await this.#axiosInstance.get<VolumeResponse>(
      CameraApiEnum.GET_VOLUME,
    );
  };

  public getDeviceSN = async () => {
    return await this.#axiosInstance.get<GetDeviceSNResponse>(
      CameraApiEnum.GET_DEVICE_SN,
    );
  };

  public getVersionInfo = async () => {
    return await this.#axiosInstance.get<GetVersionInfoResponse>(
      CameraApiEnum.GET_VERSIONS,
    );
  };

  public getBatteryInfo = async () => {
    return await this.#axiosInstance.get<GetBatteryInfoResponse>(
      CameraApiEnum.GET_BATTERY_INFO,
    );
  };

  public getStorage = async () => {
    return await this.#axiosInstance.get<GetStorageResponse>(
      CameraApiEnum.GET_STORAGE,
    );
  };

  public formatStorage = async () => {
    this.#log('Camera format storage has been called');
    return await this.#axiosInstance.get(CameraApiEnum.FORMAT_STORAGE);
  };

  public static loadCameraData = async (
    ip: string,
    cameraIpRequiredMsg: string,
  ) => {
    const cameraInstance = CameraConnection.getInstance(
      ip,
      cameraIpRequiredMsg,
    );

    const [batteryInfo, volume, storage, versionInfo, deviceSN, gain] =
      await Promise.all([
        cameraInstance.getBatteryInfo(),
        cameraInstance.getVolume(),
        cameraInstance.getStorage(),
        cameraInstance.getVersionInfo(),
        cameraInstance.getDeviceSN(),
        cameraInstance.getGain(),
      ]);

    const cameraData = {
      ip,
      batteryInfo: batteryInfo.data.obj,
      volume: volume.data.obj,
      storage: storage.data.obj,
      versionInfo: versionInfo.data.obj,
      deviceSN: deviceSN.data.data,
      sideGain: gain.sideGain,
      mainGain: gain.mainGain,
    };

    cameraInstance.#log(
      `Camera data has been loaded: ${JSON.stringify(cameraData)}`,
    );

    Sentry.setTags({
      cameraIp: ip,
      deviceSN: deviceSN.data.data,
      sideGain: gain.sideGain.data,
      mainGain: gain.mainGain.data,
    });

    return cameraData;
  };

  public getSettings = async () => {
    const settings = await this.#axiosInstance.get<GetSettingsResponse>(
      CameraApiEnum.GET_SETTINGS,
    );

    this.#log(`Camera settings has been loaded: ${JSON.stringify(settings)}`);

    return settings;
  };

  public getSideGain = async () => {
    return await this.#axiosInstance.get<GainResponse>(
      CameraApiEnum.GET_SIDE_GAIN,
    );
  };

  public getMainGain = async () => {
    return await this.#axiosInstance.get<GainResponse>(
      CameraApiEnum.GET_MAIN_GAIN,
    );
  };

  public getGain = async (): Promise<{
    sideGain: GainResponse;
    mainGain: GainResponse;
  }> => {
    const [sideGain, mainGain] = await Promise.all([
      this.getSideGain(),
      this.getMainGain(),
    ]);

    return { sideGain: sideGain.data, mainGain: mainGain.data };
  };

  public say = async (message: string) => {
    return await this.#axiosInstance.get(CameraApiEnum.SAY, {
      params: {
        txt: message,
        loop: 0,
        delay: 0,
      },
    });
  };

  public powerOff = async () => {
    this.#log('Camera power off has been called');
    return await this.#axiosInstance.get(CameraApiEnum.SHUT_DOWN);
  };
}

export default CameraConnection;
